import React, { useState, useRef, useMemo, useEffect } from 'react';
import '../../CSS/Jobmaster.css';
import api from "../../api.js";
import { useRights } from "../../Components/RightsContext.jsx";

// Must match this screen's Submenus.pathname exactly — verify against the
// DB before trusting this (a mismatched string silently hides everything).
const JOB_PATH = "Master/Jobmaster";

const initials = (name) => (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
const fmtMobile = (m) => (m && m.length === 10 ? m.slice(0, 5) + ' ' + m.slice(5) : m);

const EMPTY_FORM = { partyCode: '', name: '', code: '', contact: '', mobile: '', email: '', airWt: 0, surfWt: 0, vol: false, active: true, otpRequired: false };
const EMPTY_ERRORS = { partyCode: false, name: false, mobile: false, email: false };

let toastSeq = 0;

// API error responses aren't always plain strings — ASP.NET Core's [ApiController]
// auto-validation returns a ProblemDetails/ValidationProblemDetails OBJECT on 400,
// and a failed/empty response can come back as {}. Rendering that object directly
// in JSX throws "Objects are not valid as a React child". This always returns a string.
function extractErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (typeof data === 'object') {
    if (data.title) return data.title;                       // ProblemDetails
    if (data.errors) {                                        // ValidationProblemDetails
      const first = Object.values(data.errors)[0];
      if (Array.isArray(first) && first.length) return first[0];
    }
    if (data.message) return data.message;
  }
  return fallback;
}

export default function JobMaster() {
  const rights = useRights(JOB_PATH);
  const [parties, setParties] = useState([]);           // [{ Party_id, Party_Name }] — live from /dropdown?mode=PMS
  const [partiesLoading, setPartiesLoading] = useState(true);
  const [jobs, setJobs] = useState([]);                  // live from /JobMaster
  const [jobsLoading, setJobsLoading] = useState(true);
  const [curParty, setCurParty] = useState('');          // numeric Party_id (or '')
  const [editId, setEditId] = useState(null);
  const [partySearch, setPartySearch] = useState('');
  const [partyPage, setPartyPage] = useState(1);
  const PARTY_PAGE_SIZE = 7;
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [toasts, setToasts] = useState([]);

  // combobox state (Party field inside the modal)
  const [partyQuery, setPartyQuery] = useState('');
  const [partyDropdownOpen, setPartyDropdownOpen] = useState(false);
  const partyBoxRef = useRef(null);
  const nameInputRef = useRef(null);

  /* ════════ LOAD PARTIES (shared by sidebar + modal combobox) ════════ */
  useEffect(() => {
    setPartiesLoading(true);
    api.get('/dropdown?mode=PMS')
      .then((res) => setParties(res.data || []))
      .catch(() => toast('Failed to load parties', 'err'))
      .finally(() => setPartiesLoading(false));
  }, []);
//
  /* ════════ LOAD JOBS ════════ */
  function loadJobs(partyId) {
    setJobsLoading(true);
    api.get('api/JobMaster', { params: partyId ? { partyId } : {} })
      .then((res) => {
        const mapped = (res.data || []).map((j) => ({
          id: j.job_Id || "",
          // partyCode: j.party_Id || "",
          partyId: j.party_Id || "",
          partyCode: j.party_Code || "",
          code: j.job_Code || "",
          name: j.job_Name || "",
          contact: j.contact_Person || "",
          mobile: j.mobile_No || "",
          email: j.email || "",
          airWt: j.chrgwtAir || "",
          surfWt: j.chrgwtSur || "",
          // vol: j.volumetric === 'Y' || "",
          vol: j.volumetric === 'Y',
          // active: j.status === 'A' || "",
          active: j.status === 'A',
          otpRequired: j.otp_Required === 'Y' || j.otp_Required === true || j.OTP_Required === true || false,
        }));
        setJobs(mapped);
      })
      .catch(() => toast('Failed to load jobs', 'err'))
      .finally(() => setJobsLoading(false));
  }
  useEffect(() => { loadJobs(); }, []); // load all jobs once on mount

  /* ════════ CLOSE COMBOBOX ON OUTSIDE CLICK ════════ */
  useEffect(() => {
    function handleClickOutside(e) {
      if (partyBoxRef.current && !partyBoxRef.current.contains(e.target)) {
        setPartyDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // const jobsOf = (partyId) => jobs.filter((j) => j.partyCode === partyId);
  // const jobsOf = (partyId) => jobs.filter((j) => j.partyId === partyId);
  const jobsOf = (partyId) => jobs.filter((j) => String(j.partyId) === String(partyId));

  /* ════════ TOAST ════════ */
  function toast(msg, type) {
    const id = ++toastSeq;
    setToasts((t) => [...t, { id, msg, type: type || 'info', leaving: false }]);
    setTimeout(() => {
      setToasts((t) => t.map((x) => (x.id === id ? { ...x, leaving: true } : x)));
      setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 320);
    }, 2700);
  }
  const TOAST_ICONS = { ok: '✓', err: '✕', warn: '⚠', info: 'ℹ' };

  /* ════════ STATS ════════ */
  const stats = {
    parties: parties.length,
    jobsTotal: jobs.length,
    active: jobs.filter((j) => j.active).length,
    vol: jobs.filter((j) => j.vol).length,
  };

  /* ════════ PARTIES (sidebar) ════════ */
  const filteredParties = useMemo(() => {
    const q = partySearch.toLowerCase().trim();
    return parties.filter((p) => !q || (p.Party_Name || '').toLowerCase().includes(q));
  }, [partySearch, parties]);

  const partyTotalPages = Math.max(1, Math.ceil(filteredParties.length / PARTY_PAGE_SIZE));
  useEffect(() => {
    setPartyPage(1); // reset to page 1 whenever the search narrows/widens the list
  }, [partySearch, parties.length]);
  useEffect(() => {
    if (partyPage > partyTotalPages) setPartyPage(partyTotalPages);
  }, [partyTotalPages, partyPage]);
  const pagedParties = useMemo(() => {
    const start = (partyPage - 1) * PARTY_PAGE_SIZE;
    return filteredParties.slice(start, start + PARTY_PAGE_SIZE);
  }, [filteredParties, partyPage]);

  function selectParty(id) {
    setCurParty(id);
  }

  const filteredPartyOptions = useMemo(() => {
    const q = partyQuery.toLowerCase().trim();
    return parties.filter((p) => !q || (p.Party_Name || '').toLowerCase().includes(q));
  }, [partyQuery, parties]);

  function selectPartyOption(p) {
    updatePartyCode(p.Party_id);
    setPartyQuery(p.Party_Name);
    setPartyDropdownOpen(false);
  }

  /* ════════ JOB MODAL ════════ */
  function openJobModal(id) {
    const j = id ? jobs.find((x) => x.id === id) : null;
    // const partyCode = j ? j.partyCode : curParty;
    const partyId = j ? j.partyId : curParty; 
    setEditId(id || null);
    setForm({
      // partyCode: partyCode || '',
      partyCode: partyId || '', 
      name: j ? j.name : '',
      code: j ? j.code : '',
      contact: j ? j.contact : '',
      mobile: j ? j.mobile : '',
      email: j ? j.email : '',
      airWt: j ? j.airWt : 0,
      surfWt: j ? j.surfWt : 0,
      vol: j ? j.vol : false,
      active: j ? j.active : true,
      otpRequired: j ? j.otpRequired : false,
    });
    // const partyLabel = partyCode ? ((parties.find((p) => p.Party_id === partyCode) || {}).Party_Name || '') : '';
    const partyLabel = partyId ? ((parties.find((p) => p.Party_id === partyId) || {}).Party_Name || '') : '';
    setPartyQuery(partyLabel);
    setErrors(EMPTY_ERRORS);
    setModalOpen(true);
    // setTimeout(() => {
    //   if (!partyCode) return; // let user pick party first
    //   nameInputRef.current && nameInputRef.current.focus();
    // }, 120);
    setTimeout(() => {
    if (!partyId) return;
    nameInputRef.current && nameInputRef.current.focus();
  }, 120);
  }
  function closeModal() {
    setModalOpen(false);
  }

  function updatePartyCode(code) {
    setForm((f) => ({ ...f, partyCode: code }));
    if (errors.partyCode) setErrors((e) => ({ ...e, partyCode: false }));
  }

  function toggleWeightFields(checked) {
    setForm((f) => ({ ...f, vol: checked, airWt: checked ? f.airWt : 0, surfWt: checked ? f.surfWt : 0 }));
  }

  function updateField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: false }));
  }

  /* ════════ SAVE / UPDATE (both POST — PUT/DELETE are blocked on IIS) ════════ */
  async function saveJob() {
    const name = form.name.trim();
    // const mobileRaw = form.mobile.replace(/\D/g, '').replace(/^91(?=\d{10}$)/, '');
    const mobileRaw = (form.mobile || "").replace(/\D/g, "") .replace(/^91(?=\d{10}$)/, "");
    const email = form.email.trim();
    let ok = true;
    const newErrors = { partyCode: false, name: false, mobile: false, email: false };
    if (!form.partyCode) { newErrors.partyCode = true; ok = false; }
    if (!name) { newErrors.name = true; ok = false; }
    if (!/^[6-9]\d{9}$/.test(mobileRaw)) { newErrors.mobile = true; ok = false; }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { newErrors.email = true; ok = false; }
    if (!ok) {
      setErrors(newErrors);
      toast('Please fix the highlighted fields', 'warn');
      return;
    }

    const payload = {
      Party_Id: form.partyCode,
      Job_Name: name,
      Contact_Person: form.contact.trim(),
      Mobile_No: mobileRaw,
      Email: email,
      Volumetric: form.vol,
      ChrgwtAir: form.vol ? (parseFloat(form.airWt) || 0) : 0,
      ChrgwtSur: form.vol ? (parseFloat(form.surfWt) || 0) : 0,
      Active: form.active,
      OTP_Required: form.otpRequired,
    };

    try {
      if (editId) {
        await api.post('api/JobMaster/update', { Job_Id: editId, ...payload });
        toast('Job updated — ' + name, 'ok');
      } else {
        await api.post('api/JobMaster/save', payload);
        toast('Job added — ' + name, 'ok');
      }
      setCurParty(form.partyCode);
      closeModal();
      loadJobs(); // refresh from server so counts/lists reflect the real DB state
    } catch (err) {
      toast(extractErrorMessage(err, 'Failed to save job'), 'err');
    }
  }

  async function delJob(id) {
    const j = jobs.find((x) => x.id === id);
    if (!j) return;
    if (!window.confirm('Delete job "' + j.name + '" (' + j.code + ')?')) return;
    try {
      await api.post('api/JobMaster/delete', id);
      toast('Job deleted', 'info');
      loadJobs();
    } catch {
      toast('Failed to delete job', 'err');
    }
  }

  const activeParty = parties.find((x) => x.Party_id === curParty);
  const activeJobs = curParty ? jobsOf(curParty) : [];

  return (
    <div className="job-root">
      {/* <header className="job-hdr">
        <div className="job-logo">
          <div className="job-logo-mark">
            <svg viewBox="0 0 24 24"><path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm13.5 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" /></svg>
          </div>
          <div>
            <div className="job-logo-name">Saami <span>Tradestar</span></div>
            <div className="job-logo-pvt">Job Master</div>
          </div>
        </div>
        <div className="job-hdr-user"><div className="job-hdr-av">AD</div>Admin</div>
      </header>

      <div className="job-bc"><span>Master</span><span className="job-bc-sep">›</span><span className="job-bc-cur">Job Master</span></div> */}

      <div className="job-page">
        <div className="job-pg-row">
          <div className="job-pg-title">
            <h1>Job Master</h1>
            <p>Create and manage multiple jobs against each party. Every job carries its own mobile number and charged-weight settings.</p>
          </div>
        </div>

        <div className="job-stat-row">
          <div className="job-stat-box"><div className="job-sb-val">{stats.parties}</div><div className="job-sb-lbl">Parties</div></div>
          <div className="job-stat-box job-teal"><div className="job-sb-val">{stats.jobsTotal}</div><div className="job-sb-lbl">Total Jobs</div></div>
          <div className="job-stat-box job-green"><div className="job-sb-val">{stats.active}</div><div className="job-sb-lbl">Active Jobs</div></div>
          <div className="job-stat-box job-blue"><div className="job-sb-val">{stats.vol}</div><div className="job-sb-lbl">Volumetric Jobs</div></div>
        </div>

        <div className="job-layout">
          {/* PARTIES */}
          <div className="job-card">
            <div className="job-band job-navy-band">
              <div className="job-band-icon"><svg viewBox="0 0 24 24" strokeWidth="2"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /></svg></div>
              <div><div className="job-band-title">Parties</div></div>
              <div className="job-band-r"><span className="job-band-badge">{parties.length}</span></div>
            </div>
            <div className="job-party-search">
              <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
              <input value={partySearch} onChange={(e) => setPartySearch(e.target.value)} placeholder="Search party name…" />
            </div>
            <div className="job-party-list">
              {partiesLoading ? (
                <div className="job-empty" style={{ padding: '30px' }}><div className="job-e-s">Loading parties…</div></div>
              ) : !filteredParties.length ? (
                <div className="job-empty" style={{ padding: '30px' }}><div className="job-e-s">No parties match</div></div>
              ) : (
                pagedParties.map((p) => {
                  const n = jobsOf(p.Party_id).length;
                  return (
                    <div key={p.Party_id} className={'job-party-item' + (curParty === p.Party_id ? ' job-active' : '')} onClick={() => selectParty(p.Party_id)}>
                      <div className="job-pi-av">{initials(p.Party_Name)}</div>
                      <div className="job-pi-main"><div className="job-pi-name">{p.Party_Name}</div>
                      {/* <div className="job-pi-code">ID {p.Party_id}</div> */}
                      <div className="job-pi-code">Code {p.Party_Code}</div></div>
                      <span className={'job-pi-count' + (n ? '' : ' job-zero')}>{n} job{n !== 1 ? 's' : ''}</span>
                    </div>
                  );
                })
              )}
            </div>
            {!partiesLoading && filteredParties.length > PARTY_PAGE_SIZE && (
              <div className="job-pagination">
                <button
                  type="button"
                  className="job-page-btn"
                  disabled={partyPage <= 1}
                  onClick={() => setPartyPage((pg) => Math.max(1, pg - 1))}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                </button>
                <span className="job-page-info">Page {partyPage} of {partyTotalPages}</span>
                <button
                  type="button"
                  className="job-page-btn"
                  disabled={partyPage >= partyTotalPages}
                  onClick={() => setPartyPage((pg) => Math.min(partyTotalPages, pg + 1))}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
              </div>
            )}
          </div>

          {/* JOBS */}
          <div className="job-card">
            {!activeParty ? (
              <div className="job-empty">
                <div className="job-e-ico">👈</div>
                <div className="job-e-t">Select a party</div>
                <div className="job-e-s">Choose a party on the left to view and manage its jobs</div>
                {rights.add && (
                  <button className="job-btn job-btn-primary" style={{ marginTop: '14px' }} onClick={() => openJobModal()}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg> Add Job
                  </button>
                )}
              </div>
            ) : (
              <div>
                <div className="job-jobs-head">
                  <div className="job-jh-av">{initials(activeParty.Party_Name)}</div>
                  <div>
                    <div className="job-jh-name">{activeParty.Party_Name}</div>
                    <div className="job-jh-meta">Party Code: {activeParty.Party_Code} · {activeJobs.length} job{activeJobs.length !== 1 ? 's' : ''}</div>
                  </div>
                  <div className="job-jh-r">
                    {rights.add && (
                      <button className="job-btn job-btn-primary" onClick={() => openJobModal()}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg> Add Job
                      </button>
                    )}
                  </div>
                </div>
                <div className="job-jobs-body">
                  {jobsLoading ? (
                    <div className="job-empty"><div className="job-e-s">Loading jobs…</div></div>
                  ) : !activeJobs.length ? (
                    <div className="job-empty">
                      <div className="job-e-ico">🗂️</div>
                      <div className="job-e-t">No jobs yet</div>
                      <div className="job-e-s">Add the first job for {activeParty.Party_Name}</div>
                      {rights.add && (
                        <button className="job-btn job-btn-primary" style={{ marginTop: '14px' }} onClick={() => openJobModal()}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg> Add Job
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="job-job-grid">
                      {activeJobs.map((j) => (
                        <div key={j.id} className={'job-job-card' + (j.active ? '' : ' job-inactive')}>
                          <div className="job-jc-top">
                            <div><div className="job-jc-name">{j.name}</div><div className="job-jc-code">{j.code}</div></div>
                            <div className="job-jc-status"><span className={'job-pill ' + (j.active ? 'job-pill-active' : 'job-pill-inactive')}>{j.active ? 'Active' : 'Inactive'}</span></div>
                          </div>
                          <div className="job-jc-body">
                            <div className="job-jc-contact">
                              <svg viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0122 16.92z" /></svg>
                              <div>
                                <div className="job-jc-mobile">+91 {fmtMobile(j.mobile)}</div>
                                {j.contact ? <div className="job-jc-person">{j.contact}</div> : null}
                              </div>
                            </div>
                            {j.email ? <div className="job-jc-row"><span className="job-jc-k">Email</span><span className="job-jc-v" style={{ fontSize: '11.5px' }}>{j.email}</span></div> : null}
                            {j.vol ? (
                              <>
                                <div className="job-jc-row"><span className="job-jc-k">Air Charged Wt</span><span className="job-jc-v">{(+j.airWt).toFixed(1)} kg</span></div>
                                <div className="job-jc-row"><span className="job-jc-k">Surface Charged Wt</span><span className="job-jc-v">{(+j.surfWt).toFixed(1)} kg</span></div>
                              </>
                            ) : (
                              <div className="job-jc-row"><span className="job-jc-k">Charged Weight</span><span className="job-jc-v">By actual weight</span></div>
                            )}
                            <div className="job-jc-tags">
                              <span className={'job-jc-tag' + (j.vol ? '' : ' job-off')}>{j.vol ? '✓' : '✕'} Volumetric</span>
                              <span className={'job-jc-tag' + (j.otpRequired ? '' : ' job-off')}>{j.otpRequired ? '✓' : '✕'} OTP Required</span>
                            </div>
                          </div>
                          <div className="job-jc-foot">
                            {rights.edit && (
                              <button className="job-ic-btn job-ic-edit" onClick={() => openJobModal(j.id)}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                              </button>
                            )}
                            {/* {rights.delete && (
                              <button className="job-ic-btn job-ic-del" onClick={() => delJob(j.id)}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" /></svg>
                              </button>
                            )} */}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* JOB MODAL */}
      <div className={'job-modal-overlay' + (modalOpen ? ' job-show' : '')} onMouseDown={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
        <div className="job-modal">
          <div className="job-modal-head">
            <div className="job-mh-ico"><svg viewBox="0 0 24 24" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" /></svg></div>
            <div>
              <div className="job-mh-title">{editId ? 'Edit Job' : 'Add Job'}</div>
              <div className="job-mh-sub">{form.partyCode ? (parties.find((p) => p.Party_id === form.partyCode) || {}).Party_Name : 'Select a party below'}</div>
            </div>
            <button className="job-modal-x" onClick={closeModal}>✕</button>
          </div>
          <div className="job-modal-body">
            <div className="job-sec-lbl">Party</div>
            <div className="job-field" ref={partyBoxRef}>
              <label>Party <span className="job-req">*</span></label>
              <div className="job-combo">
                <input
                  type="text"
                  className={'job-combo-input' + (errors.partyCode ? ' job-err' : '')}
                  placeholder="Search party…"
                  value={partyQuery}
                  disabled={!!editId}
                  onChange={(e) => {
                    setPartyQuery(e.target.value);
                    setPartyDropdownOpen(true);
                    if (form.partyCode) updatePartyCode('');
                  }}
                  onFocus={() => !editId && setPartyDropdownOpen(true)}
                />
                <button
                  type="button"
                  className="job-combo-arrow"
                  disabled={!!editId}
                  onClick={() => !editId && setPartyDropdownOpen((o) => !o)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
                </button>
                {partyDropdownOpen && !editId && (
                  <div className="job-combo-list">
                    {!filteredPartyOptions.length ? (
                      <div className="job-combo-empty">No party found</div>
                    ) : (
                      filteredPartyOptions.map((p) => (
                        <div
                          key={p.Party_id}
                          className={'job-combo-item' + (form.partyCode === p.Party_id ? ' job-combo-item-active' : '')}
                          onClick={() => selectPartyOption(p)}
                        >
                          {p.Party_Name}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div className={'job-field-err' + (errors.partyCode ? ' job-show' : '')}>Please select a party</div>
            </div>
            <div className="job-sec-lbl">Job Details</div>
            <div className="job-g2">
              <div className="job-field">
                <label>Job Name <span className="job-req">*</span></label>
                <input ref={nameInputRef} type="text" className={errors.name ? 'job-err' : ''} value={form.name} onChange={(e) => updateField('name', e.target.value)} placeholder="e.g. Spare Parts Distribution" />
                <div className={'job-field-err' + (errors.name ? ' job-show' : '')}>Job name is required</div>
              </div>
              <div className="job-field">
                <label>Job Code <span className="job-hint">(auto)</span></label>
                <input type="text" readOnly value={form.code || 'Generated on save'} />
              </div>
            </div>
            <div className="job-sec-lbl">Contact</div>
            <div className="job-field">
              <label>Contact Person</label>
              <input type="text" value={form.contact} onChange={(e) => updateField('contact', e.target.value)} placeholder="Coordinator name (optional)" />
            </div>
            <div className="job-g2">
              <div className="job-field">
                <label>Mobile No <span className="job-req">*</span></label>
                <div className="job-mobile-wrap">
                  <span className="job-mobile-cc">+91</span>
                  <input type="tel" className={'job-mono' + (errors.mobile ? ' job-err' : '')} maxLength={11} placeholder="10-digit mobile" inputMode="numeric" value={form.mobile} onChange={(e) => updateField('mobile', e.target.value)} />
                </div>
                <div className={'job-field-err' + (errors.mobile ? ' job-show' : '')}>Enter a valid 10-digit mobile</div>
              </div>
              <div className="job-field">
                <label>Email</label>
                <input type="email" className={errors.email ? 'job-err' : ''} value={form.email} onChange={(e) => updateField('email', e.target.value)} placeholder="job@party.com" />
                <div className={'job-field-err' + (errors.email ? ' job-show' : '')}>Enter a valid email</div>
              </div>
            </div>
            <div className="job-sec-lbl">Charged Weight Settings</div>
            <div className="job-tog-row">
              <div className="job-tog-info"><div className="job-tog-name">Volumetric</div><div className="job-tog-desc">Apply volumetric weight calculation</div></div>
              <label className="job-switch">
                <input type="checkbox" checked={form.vol} onChange={(e) => toggleWeightFields(e.target.checked)} />
                <span className="job-slider"></span>
              </label>
            </div>
            <div className={'job-g2 job-wt-fields' + (form.vol ? '' : ' job-locked')}>
              <div className="job-field">
                <label>Air Charged Wt <span className="job-hint">(kg)</span></label>
                <input type="number" className="job-mono" min="0" step="0.1" disabled={!form.vol} value={form.airWt} onChange={(e) => updateField('airWt', e.target.value)} />
              </div>
              <div className="job-field">
                <label>Surface Charged Wt <span className="job-hint">(kg)</span></label>
                <input type="number" className="job-mono" min="0" step="0.1" disabled={!form.vol} value={form.surfWt} onChange={(e) => updateField('surfWt', e.target.value)} />
              </div>
            </div>
            <div className={'job-wt-note' + (!form.vol ? ' job-show' : '')}>Enable <b>Volumetric</b> to set charged-weight values.</div>
            <div className="job-sec-lbl">Status</div>
            <div className="job-tog-row">
              <div className="job-tog-info"><div className="job-tog-name">Active</div><div className="job-tog-desc">Job is available for transactions</div></div>
              <label className="job-switch">
                <input type="checkbox" checked={form.active} onChange={(e) => updateField('active', e.target.checked)} />
                <span className="job-slider"></span>
              </label>
            </div>
            <div className="job-sec-lbl">Delivery Settings</div>
            <div className="job-tog-row">
              <div className="job-tog-info"><div className="job-tog-name">OTP Required</div><div className="job-tog-desc">Consignee must provide OTP before goods are handed over, for bookings under this job</div></div>
              <label className="job-switch">
                <input type="checkbox" checked={form.otpRequired} onChange={(e) => updateField('otpRequired', e.target.checked)} />
                <span className="job-slider"></span>
              </label>
            </div>
          </div>
          <div className="job-modal-foot">
            <button className="job-btn job-btn-ghost" onClick={closeModal}>Cancel</button>
            <button className="job-btn job-btn-green" onClick={saveJob}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v14a2 2 0 01-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg> Save Job
            </button>
          </div>
        </div>
      </div>

      <div className="job-toast-wrap">
        {toasts.map((t) => (
          <div key={t.id} className={'job-toast job-' + t.type + (t.leaving ? ' job-toast-out' : '')}>
            <span style={{ fontSize: '15px' }}>{TOAST_ICONS[t.type] || 'ℹ'}</span>
            {typeof t.msg === 'string' ? t.msg : 'Something went wrong'}
          </div>
        ))}
      </div>
    </div>
  );
}